import { useEffect, useMemo, useState } from "react"
import type { PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog"
import {
	isExportImageSizeSupported,
	type BitDepth,
	type DimensionSide,
	type DimensionType,
} from "@/core/export-image"
import {
	estimateEncodedExportBytes,
	type ValidatedExportImageType,
} from "@/core/utils/encoded-size-check"
import {
	estimateJpegHybridBaseBytes,
	estimateJpegHybridGroupBytes,
	extractJpegPreviewFeatures,
	JPEG_SIZE_HYBRID_PARAMETERS,
	type JpegHybridEstimateItem,
	type JpegPreviewFeatures,
} from "@/core/utils/jpeg-size-estimation"
import {
	formatEstimatedExportSize,
	getEstimatedExportDimensions,
} from "@/core/utils/export-size-estimate"
import { getFileForPhoto } from "@/store/files-state"
import { fetchPreviewDataOOB } from "@/store/previews/previews-processing"
import { PreviewSize } from "@/store/previews/previews-types"

const JPEG_ESTIMATE_DEBOUNCE_MS = 200
const MAX_CACHED_PREVIEW_FEATURES = 64
const previewFeatureCache = new Map<string, Promise<JpegPreviewFeatures>>()

class JpegPreviewUnavailableError extends Error {}

interface EstimatedExportSizeAssessment {
	status: "calculating" | "ready" | "unavailable"
	text: string
	hasUnsupportedCustomDimensions: boolean
}

interface EstimatedExportSizeOptions {
	projectId?: string
	photos: PhotoRecord[]
	metadata: Record<string, PhotoMetadataRecord>
	format: ValidatedExportImageType
	quality: number
	bitDepth?: BitDepth
	colorSpace: PredefinedColorSpace
	dimensionType: DimensionType
	dimensionSide: DimensionSide
	dimension: number
}

interface PreparedJpegEstimateItem extends JpegHybridEstimateItem {
	photo: PhotoRecord
}

const getPhotoEstimate = (
	photo: PhotoRecord,
	options: Omit<EstimatedExportSizeOptions, "photos" | "projectId">,
) => {
	const photoMetadata = options.metadata[photo.id]
	const [width, height] = getEstimatedExportDimensions(
		photoMetadata?.width,
		photoMetadata?.height,
		options.dimensionType,
		options.dimensionSide,
		options.dimension,
	)
	const v2EstimatedBytes = estimateEncodedExportBytes({
		format: options.format,
		width,
		height,
		quality: options.quality,
		iso: photoMetadata?.iso,
		bitDepth: options.bitDepth,
		colorSpace: options.colorSpace,
	})
	const estimatedBytes =
		options.format === "jpg"
			? estimateJpegHybridBaseBytes({
					width,
					height,
					quality: options.quality,
					iso: photoMetadata?.iso,
				})
			: v2EstimatedBytes

	return {
		estimatedBytes,
		v2EstimatedBytes,
		hasUnsupportedCustomDimensions:
			options.dimensionType === "Custom" && !isExportImageSizeSupported([width, height]),
	}
}

const getCachedPreviewFeatures = (projectId: string, photo: PhotoRecord) => {
	const key = [
		JPEG_SIZE_HYBRID_PARAMETERS.version,
		projectId,
		photo.id,
		photo.adjustmentsDigest ?? "original",
	].join(":")
	const cached = previewFeatureCache.get(key)
	if (cached) return cached

	const features = (async () => {
		const preview =
			(await fetchPreviewDataOOB(projectId, photo, PreviewSize.small)) ??
			(await fetchPreviewDataOOB(projectId, photo, PreviewSize.medium))
		if (!preview) throw new JpegPreviewUnavailableError("Current photo preview is unavailable")
		return extractJpegPreviewFeatures(preview)
	})().catch((error) => {
		previewFeatureCache.delete(key)
		throw error
	})

	previewFeatureCache.set(key, features)
	while (previewFeatureCache.size > MAX_CACHED_PREVIEW_FEATURES) {
		const oldestKey = previewFeatureCache.keys().next().value
		if (oldestKey === undefined) break
		previewFeatureCache.delete(oldestKey)
	}
	return features
}

export const useEstimatedExportSize = (
	options: EstimatedExportSizeOptions,
): EstimatedExportSizeAssessment => {
	const {
		projectId,
		photos,
		metadata,
		format,
		quality,
		bitDepth,
		colorSpace,
		dimensionType,
		dimensionSide,
		dimension,
	} = options
	const photoOptions = {
		metadata,
		format,
		quality,
		bitDepth,
		colorSpace,
		dimensionType,
		dimensionSide,
		dimension,
	}
	const baseAssessment = useMemo(() => {
		let totalBytes = 0
		let v2TotalBytes = 0
		let hasUnsupportedCustomDimensions = false
		const jpegItems: PreparedJpegEstimateItem[] = []
		const jpegItemKeys: string[] = []
		for (const photo of photos) {
			const estimate = getPhotoEstimate(photo, photoOptions)
			totalBytes += estimate.estimatedBytes
			v2TotalBytes += estimate.v2EstimatedBytes
			hasUnsupportedCustomDimensions ||= estimate.hasUnsupportedCustomDimensions
			if (format === "jpg") {
				const photoMetadata = metadata[photo.id]
				const file = getFileForPhoto(photo)
				const sourcePixelCount =
					typeof photoMetadata?.width === "number" &&
					photoMetadata.width > 0 &&
					typeof photoMetadata.height === "number" &&
					photoMetadata.height > 0
						? photoMetadata.width * photoMetadata.height
						: undefined
				const item: PreparedJpegEstimateItem = {
					id: photo.id,
					fileName: file?.name ?? photo.fileName,
					baseEstimatedBytes: estimate.estimatedBytes,
					iso: photoMetadata?.iso,
					sourceFileBytes: file?.size,
					sourcePixelCount,
					photo,
				}
				jpegItems.push(item)
				jpegItemKeys.push(
					[
						item.id,
						item.fileName,
						photo.adjustmentsDigest ?? "",
						item.baseEstimatedBytes,
						item.iso ?? "",
						item.sourceFileBytes ?? "",
						item.sourcePixelCount ?? "",
					].join(":"),
				)
			}
		}
		return {
			totalBytes,
			v2TotalBytes,
			hasUnsupportedCustomDimensions,
			jpegItems,
			jpegItemsKey: jpegItemKeys.join("|"),
		}
		// photoOptions contains only values represented by these dependencies.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		bitDepth,
		colorSpace,
		dimension,
		dimensionSide,
		dimensionType,
		format,
		metadata,
		photos,
		quality,
	])
	const requestKey = useMemo(
		() =>
			[
				JPEG_SIZE_HYBRID_PARAMETERS.modelSha256,
				projectId ?? "",
				format,
				quality,
				baseAssessment.jpegItemsKey,
			].join("|"),
		[baseAssessment.jpegItemsKey, format, projectId, quality],
	)
	const [jpegAssessmentState, setJpegAssessmentState] = useState<{
		requestKey: string
		status: "ready"
		text: string
	}>({ requestKey: "", status: "ready", text: "" })
	const hasInvalidCustomDimension =
		dimensionType === "Custom" &&
		(dimension < 1 || baseAssessment.hasUnsupportedCustomDimensions)

	useEffect(() => {
		if (format !== "jpg" || photos.length === 0 || hasInvalidCustomDimension || !projectId) {
			return
		}

		let isStale = false
		const debounceTimer = setTimeout(() => {
			void (async () => {
				try {
					const estimatedBytes = await estimateJpegHybridGroupBytes(
						baseAssessment.jpegItems,
						quality,
						(item) => getCachedPreviewFeatures(projectId, item.photo),
					)
					if (!isStale) {
						setJpegAssessmentState({
							requestKey,
							status: "ready",
							text: formatEstimatedExportSize(estimatedBytes),
						})
					}
				} catch (error) {
					if (!isStale) {
						if (!(error instanceof JpegPreviewUnavailableError)) {
							console.warn("Failed to calculate JPEG export size estimate", error)
						}
						setJpegAssessmentState({
							requestKey,
							status: "ready",
							text: formatEstimatedExportSize(baseAssessment.v2TotalBytes),
						})
					}
				}
			})()
		}, JPEG_ESTIMATE_DEBOUNCE_MS)

		return () => {
			isStale = true
			clearTimeout(debounceTimer)
		}
		// requestKey contains only values that materially affect the estimate.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestKey])

	if (photos.length === 0) {
		return {
			status: "ready",
			text: formatEstimatedExportSize(0),
			hasUnsupportedCustomDimensions: false,
		}
	}
	if (hasInvalidCustomDimension) {
		return {
			status: "unavailable",
			text: "Unavailable",
			hasUnsupportedCustomDimensions: baseAssessment.hasUnsupportedCustomDimensions,
		}
	}
	if (format !== "jpg") {
		return {
			status: "ready",
			text: formatEstimatedExportSize(baseAssessment.totalBytes),
			hasUnsupportedCustomDimensions: false,
		}
	}
	if (!projectId) {
		return {
			status: "ready",
			text: formatEstimatedExportSize(baseAssessment.v2TotalBytes),
			hasUnsupportedCustomDimensions: false,
		}
	}
	if (jpegAssessmentState.requestKey !== requestKey) {
		return {
			status: "calculating",
			text: "Calculating...",
			hasUnsupportedCustomDimensions: false,
		}
	}

	return {
		...jpegAssessmentState,
		hasUnsupportedCustomDimensions: false,
	}
}
